import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type Manager = {
  id: string;
  name: string;
  phone1: string;
  phone2: string;
  department: string;
  whatsapp: string;
};

export type ContactNumbers = {
  phone_carpentry_1: string;
  phone_carpentry_2: string;
  phone_aluminum_1: string;
  phone_aluminum_2: string;
  phone_iron_1: string;
  phone_iron_2: string;
  phone_manager: string;
  whatsapp_link: string;
  facebook_link: string;
  instagram_link: string;
  tiktok_link: string;
};

const defaults: ContactNumbers = {
  phone_carpentry_1: "774174292",
  phone_carpentry_2: "717001040",
  phone_aluminum_1: "774112551",
  phone_aluminum_2: "",
  phone_iron_1: "",
  phone_iron_2: "",
  phone_manager: "777196039",
  whatsapp_link: "https://wa.me/967774174292",
  facebook_link: "",
  instagram_link: "",
  tiktok_link: "",
};

// Map old key names to new key names for backward compatibility
const keyAliases: Record<string, string> = {
  phone_carpentry: "phone_carpentry_1",
  phone_aluminum: "phone_aluminum_1",
  main_phone: "phone_manager",
  whatsapp_carpentry: "whatsapp_link",
};

export function useContactSettings() {
  const [settings, setSettings] = useState<ContactNumbers>(defaults);
  const [managers, setManagers] = useState<Manager[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch_ = async () => {
      const { data } = await supabase.from("settings").select("key, value");
      if (data) {
        const map = { ...defaults };
        let managersData: Manager[] = [];
        data.forEach((s) => {
          if (s.key === "managers_data" && s.value) {
            try { managersData = JSON.parse(s.value); } catch { }
          } else {
            // Try direct key match
            const resolvedKey = keyAliases[s.key] || s.key;
            if (resolvedKey in map) {
              (map as any)[resolvedKey] = s.value || (map as any)[resolvedKey];
            }
          }
        });
        setSettings(map);
        setManagers(managersData);
      }
      setLoading(false);
    };
    fetch_();
  }, []);

  const getMainPhone = () => settings.phone_carpentry_1 || settings.phone_manager || "774174292";
  const getWhatsAppLink = () => settings.whatsapp_link || `https://wa.me/967${getMainPhone()}`;

  const getManagersByDept = (dept: string) => managers.filter(m => m.department === dept);

  const getDeptPhones = (dept: string): string[] => {
    const deptManagers = getManagersByDept(dept);
    if (deptManagers.length > 0) {
      return deptManagers.flatMap(m => [m.phone1, m.phone2].filter(Boolean));
    }
    if (dept === "نجارة") return [settings.phone_carpentry_1, settings.phone_carpentry_2].filter(Boolean);
    if (dept === "ألمنيوم") return [settings.phone_aluminum_1, settings.phone_aluminum_2].filter(Boolean);
    if (dept === "حديد") return [settings.phone_iron_1, settings.phone_iron_2].filter(Boolean);
    return [];
  };

  const getDeptPhone = (dept: string): string => {
    const phones = getDeptPhones(dept);
    return phones[0] || getMainPhone();
  };

  return { settings, managers, loading, getMainPhone, getWhatsAppLink, getManagersByDept, getDeptPhones, getDeptPhone };
}
